import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";

import { setAuthToken, setMe, setOtpVerified } from "@actions/sessionActions";
import { sessionContext } from "@context/Contexts";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import { thirtySecondsInMs } from "@typing/Constants";
import { TwoFactorSteps } from "@typing/Enums";
import {
  GetClientWithAccountQuery,
  PhoneNumberChannelEnum,
  TwoFactorAuthenticationCreateDocument,
  TwoFactorAuthenticationCreateMutation,
  TwoFactorAuthenticationCreateMutationVariables,
  TwoFactorAuthenticationDestroyDocument,
  TwoFactorAuthenticationDestroyMutation,
  TwoFactorAuthenticationDestroyMutationVariables,
  TwoFactorAuthenticationTypeEnum,
  TwoFactorAuthenticationVerifyMutation
} from "@typing/Generated";

type Props = {
  client: Pick<GetClientWithAccountQuery["client"], "id" | "verificationPhoneNumber" | "twoFactorType"> | null;
  settingUp: boolean;
};

type ClientResponse = NonNullable<
  NonNullable<TwoFactorAuthenticationVerifyMutation["twoFactorAuthenticationVerify"]>["client"]
>;

const useTwoFactorAuthenticator = ({ client, settingUp }: Props) => {
  const myClient = useMyClient();
  const history = useHistory();

  const { dispatch } = useContext(sessionContext);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);

  const previouslyVerified = useMemo(() => !!client?.twoFactorType, [client]);

  const [step, setStep] = useState(previouslyVerified ? TwoFactorSteps.VERIFY_CODE : TwoFactorSteps.SETUP_TYPE);

  const handlePhoneNumberSuccess = () => {
    setStep(TwoFactorSteps.VERIFY_CODE);
  };

  const handleVerificationSuccess = (client: ClientResponse, jwt: string) => {
    dispatch(setMe(client));
    if (myClient && !settingUp) {
      dispatch(setAuthToken(jwt, myClient));
      dispatch(setOtpVerified(true));
      const storedPathname = sessionStorage.getItem("postLoginRedirectTo");
      sessionStorage.removeItem("postLoginRedirectTo");
      history.push(storedPathname ?? "/client");
    }
  };
  const [authenticationCreate] = useMutationWithValidation<
    TwoFactorAuthenticationCreateMutation,
    TwoFactorAuthenticationCreateMutationVariables
  >(TwoFactorAuthenticationCreateDocument, "phoneNumber");

  const [deleteTwoFactorAuthentication] = useMutationWithValidation<
    TwoFactorAuthenticationDestroyMutation,
    TwoFactorAuthenticationDestroyMutationVariables
  >(TwoFactorAuthenticationDestroyDocument, "phoneNumber");

  const handleRemoveTwoFactor = () => {
    setStep(TwoFactorSteps.SETUP_TYPE);
    deleteTwoFactorAuthentication({});
    setProvisioningUri(null);
  };

  const handleTOTPAuthenticationCreate = () => {
    authenticationCreate({
      type: TwoFactorAuthenticationTypeEnum.TOTP
    }).then(res => {
      if (res?.twoFactorAuthenticationCreate?.provisioningUri) {
        setProvisioningUri(res.twoFactorAuthenticationCreate.provisioningUri);
      }
    });
  };

  useEffect(() => {
    if (client?.verificationPhoneNumber?.verified && settingUp) return;

    let ignore = false;
    if (
      client?.id &&
      !ignore &&
      previouslyVerified &&
      step === TwoFactorSteps.VERIFY_CODE &&
      client.twoFactorType === TwoFactorAuthenticationTypeEnum.SMS
    ) {
      authenticationCreate({
        channel: PhoneNumberChannelEnum.SMS,
        type: TwoFactorAuthenticationTypeEnum.SMS
      });
    }

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resendCode = useCallback(
    (channel: PhoneNumberChannelEnum) => {
      if (resendDisabled) {
        setShowLoader(true);
      } else {
        setResendDisabled(true);

        authenticationCreate({
          channel,
          type: TwoFactorAuthenticationTypeEnum.SMS
        }).finally(() => {
          setTimeout(() => {
            setResendDisabled(false);
            setShowLoader(false);
          }, thirtySecondsInMs);
        });
      }
    },
    [resendDisabled, authenticationCreate]
  );

  return {
    handlePhoneNumberSuccess,
    handleRemoveTwoFactor,
    handleTOTPAuthenticationCreate,
    handleVerificationSuccess,
    provisioningUri,
    resendCode,
    resendDisabled,
    setStep,
    showLoader,
    step
  };
};

export default useTwoFactorAuthenticator;
